// ============================================================
//  SUPPORT MESSAGE BUTTON — ADMIN DASHBOARD
//  Path: src/components/SupportMessageButton.tsx
//
//  NEW FILE. A "Message in app" button for the Expert and Customer detail
//  pages. It opens a small form and sends a one-way message into that
//  person's Messages inbox in the app (with a push notification) — no
//  email address to type, no help request needed first.
//
//  appType decides which app gets it. It matters for a person who has
//  both an expert and a customer account: the message goes to the app of
//  the page you are on.
//
//  Customers have no action buttons yet (the server's allow-list for
//  them is empty), so the button picker only shows for experts.
// ============================================================

import React, { useState } from 'react'
import { sendSupportMessageApi, getSupportOptionsApi, SupportOptions } from '../api/support.api'

const errText = (e: any, fallback: string) => e?.response?.data?.message || fallback

const SupportMessageButton: React.FC<{
  userId: string
  name: string
  appType: 'expert' | 'customer'
}> = ({ userId, name, appType }) => {
  const [open, setOpen] = useState(false)
  const [options, setOptions] = useState<SupportOptions | null>(null)
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [action, setAction] = useState('')
  const [sending, setSending] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [sentNote, setSentNote] = useState<string | null>(null)

  const openForm = async () => {
    setOpen(true)
    setErr(null)
    setSentNote(null)
    if (!options) {
      try {
        const res = await getSupportOptionsApi()
        setOptions(res.data.data)
      } catch { /* only the optional button list is lost */ }
    }
  }

  const close = () => { setOpen(false); setTitle(''); setBody(''); setAction(''); setErr(null) }

  const actionList = options?.actionScreens?.[appType] ?? {}
  const canSend = title.trim() && body.trim() && !sending

  const send = async () => {
    setSending(true)
    setErr(null)
    try {
      await sendSupportMessageApi({
        userId, appType,
        title: title.trim(), body: body.trim(), ...(action ? { actionScreen: action } : {}),
      })
      close()
      setSentNote(`Message sent to ${name || 'this person'}.`)
      setTimeout(() => setSentNote(null), 6000)
    } catch (e) {
      setErr(errText(e, 'Could not send the message'))
    } finally {
      setSending(false)
    }
  }

  return (
    <>
      <button onClick={openForm}
        className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 bg-surface-tertiary hover:bg-border-light text-gray-200 rounded-lg text-xs font-medium transition-colors">
        💬 Message in app
      </button>
      {sentNote && <span className="ml-3 text-xs text-green-400">{sentNote}</span>}

      {open && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onClick={close}>
          <div className="bg-surface-card border border-border-light rounded-2xl p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-white mb-1">Message {name || 'this person'}</h2>
            <p className="text-xs text-gray-500 mb-4">
              Goes into their Messages inbox in the {appType === 'expert' ? 'expert' : 'customer'} app, with a notification. They cannot reply there.
            </p>

            <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} placeholder="Title"
              className="w-full mb-3 bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-white placeholder-gray-500 focus:border-brand-500 focus:outline-none" />
            <textarea value={body} onChange={(e) => setBody(e.target.value)} maxLength={2000} rows={6} placeholder="Message — short and clear"
              className="w-full mb-1 bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-white placeholder-gray-500 focus:border-brand-500 focus:outline-none" />
            <p className="text-xs text-gray-500 mb-3 text-right">{body.length}/2000</p>

            {Object.keys(actionList).length > 0 && (
              <select value={action} onChange={(e) => setAction(e.target.value)}
                className="w-full mb-3 bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-white focus:border-brand-500 focus:outline-none">
                <option value="">No button</option>
                {Object.entries(actionList).map(([screen, label]) => <option key={screen} value={screen}>Button: {label as string}</option>)}
              </select>
            )}

            {err && <p className="text-sm text-red-400 mb-3">{err}</p>}
            <div className="flex justify-end gap-2">
              <button onClick={close} className="px-4 py-2 bg-surface-tertiary text-gray-300 hover:bg-border-light rounded-xl text-sm">Cancel</button>
              <button disabled={!canSend} onClick={send}
                className="px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white rounded-xl text-sm disabled:opacity-50">{sending ? 'Sending…' : 'Send'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default SupportMessageButton